// Minimal Markdown → React renderer for admin-authored editorial content
// (newsletter issues). Deliberately small: headings, paragraphs, bullet
// lists, horizontal rules, bold, italic, [links](url) and bare URLs.
//
// We render React elements rather than HTML strings, so there is no
// dangerouslySetInnerHTML and no sanitiser to keep up to date. Anything the
// grammar doesn't recognise is emitted as plain text.
//
// Single line breaks inside a paragraph become <br>, so a pasted list of
// "Title, Place. Date" lines reads the way it was written.

const HEADING_RE = /^(#{1,3})\s+(.*)$/;
const BULLET_RE = /^\s*[-*]\s+(.*)$/;
const HR_RE = /^\s*(-{3,}|\*{3,}|_{3,})\s*$/;

export default function Markdown({ source, className = '' }) {
  const blocks = parseBlocks(source ?? '');
  return (
    <div className={`md ${className}`.trim()}>
      {blocks.map((block, i) => renderBlock(block, i))}
    </div>
  );
}

function parseBlocks(text) {
  const lines = text.replace(/\r\n?/g, '\n').split('\n');
  const blocks = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) {
      i += 1;
      continue;
    }
    if (HR_RE.test(line)) {
      blocks.push({ type: 'hr' });
      i += 1;
      continue;
    }
    const heading = line.match(HEADING_RE);
    if (heading) {
      blocks.push({ type: 'heading', level: heading[1].length, text: heading[2].trim() });
      i += 1;
      continue;
    }
    if (BULLET_RE.test(line)) {
      const items = [];
      while (i < lines.length && BULLET_RE.test(lines[i])) {
        items.push(lines[i].match(BULLET_RE)[1]);
        i += 1;
      }
      blocks.push({ type: 'list', items });
      continue;
    }
    const para = [];
    while (
      i < lines.length &&
      lines[i].trim() &&
      !HEADING_RE.test(lines[i]) &&
      !BULLET_RE.test(lines[i]) &&
      !HR_RE.test(lines[i])
    ) {
      para.push(lines[i]);
      i += 1;
    }
    blocks.push({ type: 'paragraph', lines: para });
  }
  return blocks;
}

function renderBlock(block, key) {
  switch (block.type) {
    case 'hr':
      return <hr key={key} className="md-hr" />;
    case 'heading': {
      // The page title is the h1, so both '#' and '##' become h2 section
      // headings (styled differently) and '###' an h3 — keeps the outline
      // valid for screen readers whichever level authors start at.
      // Class names must be literal strings: Tailwind only emits @layer
      // rules whose names it finds verbatim in the source files.
      const Tag = block.level === 3 ? 'h3' : 'h2';
      const cls = block.level === 1 ? 'md-h1' : block.level === 2 ? 'md-h2' : 'md-h3';
      return (
        <Tag key={key} className={cls}>
          {renderInline(block.text)}
        </Tag>
      );
    }
    case 'list':
      return (
        <ul key={key} className="md-ul">
          {block.items.map((item, j) => (
            <li key={j}>{renderInline(item)}</li>
          ))}
        </ul>
      );
    case 'paragraph':
    default:
      return (
        <p key={key} className="md-p">
          {block.lines.map((line, j) => (
            <span key={j}>
              {j > 0 && <br />}
              {renderInline(line)}
            </span>
          ))}
        </p>
      );
  }
}

// ── inline ──
// Tokenises one line into text / strong / em / link nodes. Order matters:
// explicit [text](url) links first, then bare URLs and emails, then
// emphasis. Emphasis is parsed inside link text but links are not parsed
// inside emphasis — good enough for newsletter prose.

const INLINE_RE =
  /\[([^\]]+)\]\(([^)\s]+)\)|(https?:\/\/[^\s<>()]+[^\s<>().,;:!?'"])|([\w.+-]+@[\w-]+\.[\w.-]+\w)/g;

function renderInline(text) {
  const out = [];
  let last = 0;
  let key = 0;
  for (const m of text.matchAll(INLINE_RE)) {
    if (m.index > last) out.push(...renderEmphasis(text.slice(last, m.index), key++));
    if (m[1] != null) {
      out.push(
        <a key={key++} href={safeHref(m[2])} className="md-a" {...externalProps(m[2])}>
          {renderEmphasis(m[1], 0)}
        </a>,
      );
    } else if (m[3] != null) {
      out.push(
        <a key={key++} href={m[3]} className="md-a" {...externalProps(m[3])}>
          {m[3].replace(/^https?:\/\//, '')}
        </a>,
      );
    } else if (m[4] != null) {
      out.push(
        <a key={key++} href={`mailto:${m[4]}`} className="md-a">
          {m[4]}
        </a>,
      );
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(...renderEmphasis(text.slice(last), key++));
  return out;
}

const EMPHASIS_RE = /\*\*([^*]+)\*\*|\*([^*\n]+)\*|_([^_\n]+)_/g;

function renderEmphasis(text, baseKey) {
  const out = [];
  let last = 0;
  let n = 0;
  for (const m of text.matchAll(EMPHASIS_RE)) {
    if (m.index > last) out.push(text.slice(last, m.index));
    if (m[1] != null) out.push(<strong key={`${baseKey}-${n++}`}>{m[1]}</strong>);
    else out.push(<em key={`${baseKey}-${n++}`}>{m[2] ?? m[3]}</em>);
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

// Only allow http(s), mailto and site-relative links; anything else (e.g.
// javascript:) is neutralised to "#".
function safeHref(url) {
  if (/^(https?:\/\/|mailto:|\/)/i.test(url)) return url;
  if (/^[\w.-]+\.[a-z]{2,}(\/|$)/i.test(url)) return `https://${url}`;
  return '#';
}

function externalProps(url) {
  if (url.startsWith('/')) return {};
  return { target: '_blank', rel: 'noopener noreferrer' };
}
